import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "../index";
import {Deployment, DeploymentApi, DeploymentParameters, Parameter, Product, Resources, Service} from "@/client";
import {BASE_PATH} from "@/main";
import ServiceStore from "@/store/product";

const serviceStore = getModule(ServiceStore);

export class DeploymentModel {
    name = '';
    nameRules: Array<(v: string) => boolean | string> = [
        v => !!v || "Name is required",
        v => /^[a-zA-Z0-9]+$/.test(v) || "Name must contain only letters (a-z) and numbers (0-9)"
    ];

    expiry = 2;
    expiryRules: Array<(v: number) => boolean | string> = [
        v => !!v || "Expiry is required",
        v => v > 0 && v < 15 || "Expiry must be between 1 and 14 days"
    ];
    productRules: Array<(v: number) => boolean | string> = []

    public productModels: ProductModel[] = [];

    constructor() {
        //
    }

}

export class ServiceModel {
    service: Service
    enabled = false
    imageTag = ''
    imageTagRules: Array<(v: string) => boolean | string> = [
        v => !this.service.optional || !this.enabled || !!v || "Version is required"
    ];

    constructor(service: Service) {
        this.service = service
    }
}

export class ParameterModel {
    parameter: Parameter
    value: any
    showText = false

    valueRules: Array<(v: string) => boolean | string> = [
        v => !this.parameter.required || !!v || "Value is required"
    ];

    constructor(parameter: Parameter) {
        this.parameter = parameter

        if (parameter.inputType == 'checkbox') {
            this.value = parameter.defaultValue ? parameter.defaultValue === 'true' : false
        } else {
            this.value = parameter.defaultValue ? parameter.defaultValue : ""
        }
    }
}

export class ProductModel {

    product: Product;
    enabled = false;
    imageTag = '';
    imageTagRules: Array<(v: string) => boolean | string> = [
        v => !this.enabled || !!v || "Version is required"
    ];
    choosable = new Map<string, string>();
    choiceRules: Array<(v: string) => boolean | string> = [
        v => !this.enabled || !!v || "Selection is required"
    ];
    serviceModels: ServiceModel[] = []
    parameterModels: ParameterModel[] = []

    constructor(product: Product) {
        this.product = product

        this.product.choice.forEach(choice => {
            this.choosable.set(choice.name, choice.services[0].id)
        })

        this.product.services?.forEach(service => {
            const serviceModel = new ServiceModel(service)
            serviceModel.imageTag = service.imageTags == null || service.imageTags.length === 0 ? '' : service.imageTags[0]
            this.serviceModels.push(serviceModel)
        })

        this.product.parameters?.forEach(parameter => {
            const parameterModel = new ParameterModel(parameter)
            this.parameterModels.push(parameterModel)
        })
    }

    /**
     * Propagates the given image tag to the service models.
     * @param imageTag
     */
    public propagateImageTag(imageTag: string) {
        this.serviceModels.forEach(service => {
            const imageTags = service.service.imageTags;
            if (imageTags.length > 0 && imageTags.includes(imageTag)) {
                service.imageTag = imageTag
            }
        })
    }

}

@Module({
    store,
    dynamic: true,
    name: 'deployment',
    namespaced: true
})
export default class DeploymentStore extends VuexModule {

    newDeploymentModel: DeploymentModel = new DeploymentModel();
    isInitialized = false;
    deployments: Deployment[] = [];
    resources: Resources = {memoryMBUsed: 0, memoryMBLimit: 0}
    errorMessage = '';

    @Mutation
    public setIsInitialized(isInitialized: boolean) {
        this.isInitialized = isInitialized;
    }

    @Mutation
    public setDeployments(newDeployments: Deployment[]) {
        this.deployments = newDeployments
    }

    @Mutation
    public setResources(resources: Resources) {
        this.resources = resources
    }

    @Action
    public async loadResources() {
        const response = await new DeploymentApi({accessToken: this.context.rootState.oidcStore.access_token}, BASE_PATH).getResources()
        this.context.commit('setResources', response.data)
    }

    @Action
    public async loadDeployments() {
        const response = await new DeploymentApi({accessToken: this.context.rootState.oidcStore.access_token}, BASE_PATH).listDeployments();
        this.context.commit('setDeployments', response.data)
    }

    @Action({rawError: true})
    public async startDeployment(parameters: DeploymentParameters) {
        const result = new Promise((resolve, reject) => {
            new DeploymentApi({accessToken: this.context.rootState.oidcStore.access_token}, BASE_PATH).startDeployment(parameters)
                .then(response => resolve(response))
                .catch(error => {
                    reject(error.response)
                })
        });

        await this.loadResources()
        return result
    }

    @Mutation
    public setErrorMessage(newErrorMessage: string) {
        this.errorMessage = newErrorMessage
    }

    @Action
    public async stopDeployment(name: string) {
        new DeploymentApi({accessToken: this.context.rootState.oidcStore.access_token}, BASE_PATH).stopDeployment(name)
    }

    @Action
    public async pollDeployments() {
        setInterval(this.loadDeployments, 2000);
    }

    @Action
    public async pollResources() {
        setInterval(this.loadResources, 2000)
    }

    @Action
    public async initNewDeployment() {
        serviceStore.loadServices().then(() => {
            const model = new DeploymentModel();
            for (let product of serviceStore.products) {
                const productModel = new ProductModel(product);
                productModel.product = product;
                productModel.choosable = new Map<string, string>();
                model.productModels.push(productModel);
            }

            this.context.commit('setNewDeploymentModel', model);
            this.context.commit('setIsInitialized', true);
        });
    }

    @Mutation
    public setNewDeploymentModel(deploymentModel: DeploymentModel) {
        this.newDeploymentModel = deploymentModel
    }

    @Action
    public cancelDeployment() {
        this.context.commit('setIsInitialized', false)
    }
}
